// Eskom PARCC — Allocation board (admin).
//
// Every (non-deleted) transaction with its assignee, four-state review status
// and working-paper notes, so an admin can see who is reviewing what and
// allocate OR reallocate a batch of transactions to a reviewer.
//
// Filtering (owner feedback points 1 + 2): the shared 9-facet bar
// (feedback/Filters.xlsx) PLUS two allocation-specific dimensions — filter by
// reviewer (validated against the active user list, with an "Unassigned"
// sentinel) and by review status (Not Reviewed / In Progress / Pending Review /
// Reviewed).
//
// WRITE (admin-only): per-row checkboxes + select-all, a VALIDATED reviewer
// dropdown (sourced from the active access grants — no more free text), and an
// "Assign / reassign selected" button. Each selected transaction is upserted
// with assignedToEmail (the reviewer), assignedByEmail (the signed-in admin) and
// a reviewStatus that is preserved if set, else 'Not Reviewed' — so reallocation
// never resets progress. The board is admin-gated by the route; the handler
// re-confirms the admin role before writing, and appends a best-effort AuditLog
// entry. Assignment timestamp (assignedAt) lands with the connector change in P4.
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DataTable,
  Button,
  Select,
  SelectFilter,
  LoadingState,
  ErrorState,
  EmptyState,
  Truncated,
  useToast,
  type Column,
  Pager,
  usePagination,
} from '../../components/ui';
import { FacetFilterBar } from '../../components/ui/FacetFilterBar';
import { useQuery } from '@tanstack/react-query';
import { useTransactions } from '../../hooks';
import { useAuth } from '../../lib/auth';
import { canAllocate } from '../../lib/permissions';
import { adminApi } from '../../lib/adminApi';
import {
  EMPTY_FACETS,
  matchesFacets,
  facetHaystack,
  type FacetState,
  type FacetRow,
} from '../../domain/filters';
import { REVIEW_STATUS_OPTIONS } from '../../domain/enums';
import type { Transaction } from '../../data/types';
import {
  reviewerOptions,
  matchesReviewer,
  matchesReviewStatus,
  effectiveReviewStatus,
  UNASSIGNED,
} from './allocationView';

export function AllocationPage(): React.JSX.Element {
  const { grant } = useAuth();
  const { show } = useToast();
  const transactionsQuery = useTransactions();
  // The reviewers the caller may allocate to — server-scoped to their division for
  // team leads (admins/managers see all). Sourced from the admin-api so a non-admin
  // allocator never needs the admin-only ListAccessGrants query.
  const reviewersQuery = useQuery({
    queryKey: ['allocatable-reviewers', grant?.email ?? ''],
    queryFn: () => adminApi.allocatableReviewers(),
    enabled: canAllocate(grant),
  });

  const [facets, setFacets] = useState<FacetState>(EMPTY_FACETS);
  const [search, setSearch] = useState('');
  const [reviewerFilter, setReviewerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [assignReviewer, setAssignReviewer] = useState('');
  const [assigning, setAssigning] = useState(false);

  const transactions = useMemo(
    () => (transactionsQuery.data ?? []).filter((t) => !t.deleted),
    [transactionsQuery.data],
  );
  const rows: FacetRow[] = useMemo(() => transactions.map((tx) => ({ tx })), [transactions]);

  // Validated reviewer list from the active access grants (admin-only query).
  const reviewerOpts = useMemo(
    () => reviewerOptions(reviewersQuery.data?.reviewers ?? []),
    [reviewersQuery.data],
  );
  const reviewerFilterOptions = useMemo(
    () => [
      { value: '', label: 'All reviewers' },
      { value: UNASSIGNED, label: 'Unassigned' },
      ...reviewerOpts,
    ],
    [reviewerOpts],
  );
  const statusFilterOptions = useMemo(
    () => [
      { value: '', label: 'All review statuses' },
      ...REVIEW_STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
    ],
    [],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(
        (r) =>
          matchesFacets(r, facets) &&
          matchesReviewer(r.tx, reviewerFilter) &&
          matchesReviewStatus(r.tx, statusFilter) &&
          (!q || facetHaystack(r.tx).includes(q)),
      )
      .map((r) => r.tx);
  }, [rows, facets, reviewerFilter, statusFilter, search]);

  // Keys for the rows currently visible — select-all toggles over these only.
  const filteredKeys = useMemo(
    () => filtered.map((t) => t.referenceNo).filter((k): k is string => k.length > 0),
    [filtered],
  );
  const allVisibleSelected = filteredKeys.length > 0 && filteredKeys.every((k) => selected.has(k));

  const toggleOne = useCallback((ref: string, checked: boolean): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(ref);
      else next.delete(ref);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(
    (checked: boolean): void => {
      setSelected((prev) => {
        const next = new Set(prev);
        for (const k of filteredKeys) {
          if (checked) next.add(k);
          else next.delete(k);
        }
        return next;
      });
    },
    [filteredKeys],
  );

  const resetFilters = useCallback(() => {
    setFacets(EMPTY_FACETS);
    setSearch('');
    setReviewerFilter('');
    setStatusFilter('');
  }, []);

  async function handleAssign(): Promise<void> {
    // Capability re-check — the real boundary is the server (/api/allocate
    // re-derives the caller's grant and rejects out-of-scope rows); this is UX.
    if (!canAllocate(grant)) {
      show('You do not have permission to allocate transactions.', 'error');
      return;
    }
    if (!assignReviewer) {
      show('Choose a reviewer to assign.', 'error');
      return;
    }
    const refs = transactions
      .filter((t) => t.referenceNo && selected.has(t.referenceNo))
      .map((t) => t.referenceNo);
    if (refs.length === 0) {
      show('Select at least one transaction to allocate.', 'error');
      return;
    }

    setAssigning(true);
    try {
      // Server-enforced: assigns the rows in the caller's allocation scope and
      // skips the rest (a team lead is confined to their division). Audit +
      // reviewer notification are stamped server-side.
      const { assigned, skipped } = await adminApi.allocate(refs, assignReviewer);
      if (assigned > 0) {
        const tail = skipped > 0 ? ` (${skipped} outside your division were skipped)` : '';
        show(
          `Allocated ${assigned} transaction(s) to ${assignReviewer}${tail}.`,
          skipped > 0 ? 'error' : 'success',
        );
        setSelected(new Set()); // clear on success, ready for the next batch
        void transactionsQuery.refetch();
      } else {
        show(
          skipped > 0
            ? `None allocated — all ${skipped} selected transaction(s) are outside your division.`
            : 'No transactions were allocated. Please retry.',
          'error',
        );
      }
    } catch (err) {
      show(err instanceof Error ? err.message : 'Failed to allocate. Please retry.', 'error');
    } finally {
      setAssigning(false);
    }
  }

  // Bound the board — both themes get a pager (Lumen 7–8/page, Legacy 50).
  const pagination = usePagination(filtered);

  const columns = useMemo<Column<Transaction>[]>(
    () => [
      {
        key: 'select',
        header: (
          <input
            type="checkbox"
            aria-label="Select all visible transactions"
            checked={allVisibleSelected}
            disabled={assigning || filteredKeys.length === 0}
            onChange={(e) => toggleAllVisible(e.target.checked)}
          />
        ),
        sortable: false,
        accessor: () => '',
        render: (t) =>
          t.referenceNo ? (
            <input
              type="checkbox"
              aria-label={`Select ${t.referenceNo}`}
              checked={selected.has(t.referenceNo)}
              disabled={assigning}
              onChange={(e) => toggleOne(t.referenceNo, e.target.checked)}
            />
          ) : (
            ''
          ),
      },
      {
        key: 'reference',
        header: 'Reference',
        sortable: true,
        accessor: (t) => t.referenceNo,
        render: (t) =>
          t.referenceNo ? (
            <Link to={`/transactions?ref=${encodeURIComponent(t.referenceNo)}`}>
              {t.referenceNo}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        key: 'dataset',
        header: 'Dataset',
        sortable: true,
        accessor: (t) => t.dataset,
        render: (t) => t.dataset,
      },
      {
        key: 'division',
        header: 'Division',
        sortable: true,
        accessor: (t) => t.division ?? '',
        render: (t) => t.division ?? '—',
      },
      {
        key: 'supplier',
        header: 'Supplier',
        sortable: true,
        accessor: (t) => t.supplier ?? '',
        render: (t) => t.supplier ?? '—',
      },
      {
        key: 'assignedTo',
        header: 'Assigned To',
        sortable: true,
        accessor: (t) => t.assignedToEmail ?? '',
        render: (t) => t.assignedToEmail || 'Unassigned',
      },
      {
        key: 'reviewStatus',
        header: 'Review Status',
        sortable: true,
        accessor: (t) => effectiveReviewStatus(t),
        render: (t) => effectiveReviewStatus(t),
      },
      {
        key: 'notes',
        header: 'Working Paper Notes',
        sortable: true,
        accessor: (t) => t.workingPaperNotes ?? '',
        render: (t) =>
          t.workingPaperNotes ? <Truncated text={t.workingPaperNotes} max={280} /> : '—',
      },
    ],
    [selected, allVisibleSelected, assigning, filteredKeys.length, toggleOne, toggleAllVisible],
  );

  if (transactionsQuery.isLoading) return <LoadingState message="Loading allocation board…" />;
  if (transactionsQuery.error) {
    const err = transactionsQuery.error;
    return (
      <ErrorState
        message="Could not load the allocation board."
        detail={err instanceof Error ? err.message : String(err)}
        onRetry={() => {
          void transactionsQuery.refetch();
        }}
      />
    );
  }
  if (transactions.length === 0) {
    return <EmptyState message="No transactions are available to allocate." />;
  }

  const selectedCount = transactions.filter(
    (t) => t.referenceNo && selected.has(t.referenceNo),
  ).length;
  const noReviewers = reviewerOpts.length === 0;

  return (
    <>
      <FacetFilterBar
        rows={rows}
        state={facets}
        onChange={setFacets}
        onReset={resetFilters}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ref, supplier, division, assignee…"
      >
        <SelectFilter
          label="Reviewer"
          value={reviewerFilter}
          options={reviewerFilterOptions}
          onChange={setReviewerFilter}
        />
        <SelectFilter
          label="Review status"
          value={statusFilter}
          options={statusFilterOptions}
          onChange={setStatusFilter}
        />
      </FacetFilterBar>

      <div className="card">
        <div className="section-head">
          <div>
            <h3>Allocation</h3>
            <div className="small">
              {filtered.length === transactions.length
                ? `${transactions.length.toLocaleString('en-ZA')} transaction(s)`
                : `${filtered.length.toLocaleString('en-ZA')} of ${transactions.length.toLocaleString('en-ZA')} transaction(s)`}
              {selectedCount > 0 ? ` · ${selectedCount} selected` : ''}
            </div>
          </div>
          <div className="actions">
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span className="small">Reviewer</span>
              <Select
                value={assignReviewer}
                disabled={assigning || noReviewers}
                onChange={setAssignReviewer}
                ariaLabel="Reviewer to allocate to"
                options={[
                  { value: '', label: noReviewers ? 'No reviewers available' : 'Choose reviewer…' },
                  ...reviewerOpts.map((o) => ({ value: o.value, label: o.label })),
                ]}
              />
            </label>
            <Button
              variant="primary"
              onClick={() => void handleAssign()}
              disabled={assigning || selectedCount === 0 || !assignReviewer}
            >
              {assigning
                ? 'Allocating…'
                : `Assign / reassign selected${selectedCount ? ` (${selectedCount})` : ''}`}
            </Button>
          </div>
        </div>
        <DataTable
          columns={columns}
          rows={pagination.slice}
          getRowKey={(t) => t.referenceNo || t.id}
          initialSort={{ key: 'reference', dir: 'asc' }}
          emptyMessage="No transactions match the filters."
        />
        <Pager pagination={pagination} noun="transaction" />
        <p className="small" style={{ marginTop: 12 }}>
          Select transactions, choose a reviewer and use “Assign / reassign selected” to allocate
          them. Reallocating to a different reviewer preserves the existing review status, so
          progress is never reset. Use the Reviewer filter to find a reviewer’s items and move them
          in bulk.
        </p>
      </div>
    </>
  );
}
